import AsyncWrapper from '@/components/AsyncWrapper';
import { CommanderResponse, CommanderResponseRef } from '@/components/Commander';
import LayoutAdmin from '@/components/LayoutAdmin';
import LayoutPos from '@/components/LayoutPOS';
import { BaseInfoProvider } from '@/context/BaseInfoContext';
import { BaseInfo } from '@/models/baseInfo';
import { IS_POS, IS_REAL_POS } from '@/utils/env';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import iosWeekday from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { Outlet, ScrollRestoration, useOutletContext } from 'react-router-dom';
import api from './api';
import { APIError } from './api/types';
import './app.less';
import { store } from './redux/store';
import { apiErrorHandler } from '@/utils/app';
import { Brand_Logo } from '@/utils/uiHelper';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(iosWeekday);
dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const port = location.port ? `:8083` : '';
const devHost = IS_REAL_POS ? location.host.replace(/:\d+/, '') : location.host.replace(/:\d+/, '');
export const API = IS_PROD && !IS_TEST ? '' : `//${devHost}${port}`;
export const POS_WS_API = IS_PROD && !IS_TEST ? `wss://${location.hostname}` : `ws://${devHost}${port}`;

export const APIEndpoint = `${API}/admin`;

// 仅 ant-upload 组件使用
export const ImageUploadEndpoint = `${APIEndpoint}/storage/image`;
export const ImageUploadOriginEndpoint = `${APIEndpoint}/storage/image/origin`;

export const TencentMediaEndpoint = `${APIEndpoint}/tencent/media`;

window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
  e.preventDefault();
  const reason = e.reason as APIError;
  apiErrorHandler(reason);
  console.error('Promise 异常:', e);
});

api.configure({
  msgKey: 'msg',
  beforeSend: req => {
    req.url = `${APIEndpoint}${req.url}`;
    return true;
  },
  headers: IS_POS
    ? {
        'X-Device-Id': (() => window.__POS_Device__.getUUID())(),
      }
    : {},
  rejectAdaptor: err => {
    try {
      if (err.status === 401) {
        if (!IS_POS) {
          location.href = `/login?returnUrl=${location.pathname}&${location.search.replace(/^\?/, '')}`;
        }
      } else if (err.status === 430) {
        location.href = `/selectOrg?returnUrl=${encodeURIComponent(location.pathname + location.search)}`;
      }
    } catch (e) {}
    return err;
  },
});

type Props = {};

export interface APPState {
  error: APIError;
  loaded: boolean;
  loading: boolean;
  baseInfo: BaseInfo;
}
interface ContextWrapperProp {
  children: React.ReactNode;
  onChange?: ({ error, loaded, loading, baseInfo }: APPState) => void;
}

/**
 * 包裹 BaseInfoContext
 * @param param0
 * @returns
 */
const ContextWrapperInner: React.FC<ContextWrapperProp> = ({ children, onChange }: ContextWrapperProp) => {
  const commanderRef = useRef<CommanderResponseRef>();

  return (
    <BaseInfoProvider onChange={onChange} commanderRef={commanderRef}>
      <>
        <CommanderResponse ref={commanderRef} />
        {children}
      </>
    </BaseInfoProvider>
  );
};

export const ContextWrapper: React.FC<ContextWrapperProp> = ({ children, onChange }: ContextWrapperProp) => {
  return (
    <Provider store={store}>
      <ContextWrapperInner onChange={onChange}>{children}</ContextWrapperInner>
    </Provider>
  );
};

export const App: React.FC<Props> = () => {
  const [appInfo, setState] = useState<APPState>({
    loading: false,
    loaded: false,
  } as APPState);

  useEffect(() => {
    if (appInfo.error && appInfo.error.status === 401) {
      window.location.href = `/login?returnUrl=${location.pathname}&${location.search.replace(/^\?/, '')}`;
    }
    if (appInfo.error && appInfo.error.status === 430) {
      window.location.href = `/selectOrg?returnUrl=${encodeURIComponent(location.pathname + location.search)}`;
    }
  }, [appInfo.error]);

  return (
    <ContextWrapper
      onChange={state => {
        setState(state);
      }}>
      <Helmet>
        <title>{appInfo?.baseInfo?.poiName || 'VeeYung'}</title>
        <link rel="icon" href={Brand_Logo} type="image/x-icon" />
      </Helmet>
      <Outlet context={appInfo} />
    </ContextWrapper>
  );
};

type ModuleProps = Props & {
  loading?: boolean;
  loaded?: boolean;
  error?: APIError;
};

export const AppAdmin: React.FC<ModuleProps> = () => {
  const { loaded, loading, error } = useOutletContext<APPState>();
  return (
    <AsyncWrapper isInit loading={loading} error={error} render={loaded}>
      {() => (
        <LayoutAdmin>
          <ScrollRestoration />
          <Outlet />
        </LayoutAdmin>
      )}
    </AsyncWrapper>
  );
};

export const AppPOS: React.FC<ModuleProps> = () => {
  const { loaded, loading, error } = useOutletContext<APPState>();
  return (
    <>
      <Helmet>
        <title>{'Veeyung 商户终端'}</title>
      </Helmet>
      {/* <PosErrorBoundary> */}
      <AsyncWrapper isInit loading={loading} error={error} render={loaded}>
        {() => (
          <LayoutPos>
            <ScrollRestoration />
            <Outlet />
          </LayoutPos>
        )}
      </AsyncWrapper>
      {/* </PosErrorBoundary> */}
    </>
  );
};
